// TODO: This file should be grouped together in a subdirectory with PinchZoomComponent.

'use strict';

const {PinchDetector, PanDetector, DoubleTapDetector} = require('../utils/touch');
// TODO: Move this dependency to a separate location.
const {setTransformStyle} = require('global/components/carousel/utils');
const {noop} = require('global/mixins/commonjs/basicUtils');


function getBounds(zoom, dimension) {
    return ((zoom * dimension) - dimension) / 2;
}


/**
 * Handles zooming and panning on a React component. Meant to be used with PinchZoomComponent.
 *
 * @param {Number} [options.maxZoom] Maximum zoom factor. Defaults to 2.
 * @param {Number} [options.onZoomIn] Callback to fire on zoom in.
 * @param {Number} [options.onZoomOut] Callback to fire on zoom out.
 * @param {Number} [options.onPan] Callback to fire on pan.
 */
class ZoomPanModel {
    constructor({maxZoom = 2, onZoomIn = noop, onZoomOut = noop, onPan = noop}) {
        Object.assign(this, {
            _pinchDetector: null,
            _panDetector: null,
            _doubleTapDetector: null,
            _zoomInCallback: onZoomIn,
            _zoomOutCallback: onZoomOut,
            _panCallback: onPan,
            _maxZoom: maxZoom,
            _el: null,
            _state: {}
        });

        this._setInitialState();
    }

    setEl(el) {
        this._el = el;

        // Wire up the touch detection components.
        this._pinchDetector = new PinchDetector({
            onZoomIn: (...args) => this._doCallback(() => this._onZoomIn(...args)),
            onZoomOut: (...args) => this._doCallback(() => this._onZoomOut(...args)),
            el
        });

        this._panDetector = new PanDetector({
            onPan: (...args) => this._doCallback(() => this._onPanUpdate(...args)),
            el
        });

        this._doubleTapDetector = new DoubleTapDetector({
            onDoubleTap: (...args) => this._doCallback(() => this._onDoubleTap(...args)),
            el
        });
    }

    /**
     * Enable touch detection.
     */
    enable() {
        this._state.enabled = true;
    }

    /**
     * Disable touch detection.
     */
    disable() {
        this._state.enabled = false;
    }

    _doCallback(callback) {
        // Check if touch detection is enabled and then fire the callback if it is.
        if (this._state.enabled) {
            callback();
        }
    }

    reset() {
        this._setInitialState();
    }

    _setInitialState() {
        this._state.zoom = 1;
        this._state.left = 0;
        this._state.top = 0;
        this._state.panX = 0;
        this._state.panY = 0;
        this._state.enabled = true;
    }

    destroy() {
        // The touch detectors add event listeners so we need to destroy it to prevent memory leaks.
        this._pinchDetector.destroy();
        this._panDetector.destroy();
        this._doubleTapDetector.destroy();
    }

    /**
     * Add the necessary styles based on the pan position and zoom. Meant to be called during render of the React view.
     * @param  {Object} style The style object to add properties to.
     */
    getStyle(style) {
        const {zoom, panX, panY} = this._state;

        setTransformStyle(style, `scale(${zoom}) translateX(${panX}px) translateY(${panY}px)`);
    }

    /**
     * Go to max zoom factor after a double tap.
     */
    _onDoubleTap() {
        if (this._state.zoom === this._maxZoom) {
            this._onZoomOut();
        } else {
            this._onZoomIn();
        }
    }

    /**
     * Set the current bounds on panning based on the size of the element and the current zoom factor.
     * @param {Number} zoom Current zoom factor.
     */
    _setBounds(zoom) {
        const width = this._el.clientWidth;
        const height = this._el.clientHeight;

        this._state.left = getBounds(zoom, width);
        this._state.top = getBounds(zoom, height);
    }

    _onZoomIn() {
        const zoom = this._state.zoom = this._maxZoom;

        this._setBounds(zoom);
        this._zoomInCallback();
    }

    _onZoomOut() {
        const zoom = this._state.zoom = 1;
        this._state.panX = 0;
        this._state.panY = 0;

        this._setBounds(zoom);
        this._zoomOutCallback();
    }

    /**
     * Prevent panning beyond a certain boundary so that the element cannot be dragged off screen.
     * @param  {Number} pan  Actual pan distance.
     * @param  {Number} edge Position of top or left boundary.
     * @return {Number}      Constrained pan distance.
     */
    _constrainPan(pan, edge) {
        const {zoom} = this._state;

        if (pan > 0 && Math.abs(pan * zoom) >= edge) {
            pan = edge / zoom;
        }

        if (pan < 0 && Math.abs(pan * this._state.zoom) >= edge) {
            pan = -edge / zoom;
        }

        return pan;
    }

    _onPanUpdate(distanceX, distanceY) {
        const {zoom} = this._state;

        // Constrain the actual pan distance based on the boundaries.
        this._state.panX = this._constrainPan(this._state.panX + (distanceX / zoom), this._state.left);
        this._state.panY = this._constrainPan(this._state.panY + (distanceY / zoom), this._state.top);

        this._panCallback();
    }
}


module.exports = ZoomPanModel;



// WEBPACK FOOTER //
// ./assets/js/global/components/ZoomPanModel.es.js